import { type FC, memo, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Divider,
  Grid,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle,
  EditOutlined,
  ExpandMore,
  ImageNotSupported,
  LightbulbOutlined,
} from '@mui/icons-material';

import { type FileContent } from '#apps/client-web/features/file-storage/services/file-storage';
import { type LightSourceGroup } from '#apps/client-web/pages/audit-form-lighting-type/audit-form-lighting-type-page.renderer';

import { type AuditLightingTypeFormEntry } from '#packages/data-collection/models/audit-lighting-type-form';

import { LightingTypeEntryDetails } from './lighting-type-entry-details';

const THUMB_SIZE = 72;

const EntryThumbnail: FC<{ content: FileContent | null }> = ({ content }) => {
  const [src, setSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!content) {
      setSrc(null);
      return;
    }
    const blob = content instanceof Blob ? content : new Blob([content]);
    const url = URL.createObjectURL(blob);
    setSrc(url);
    return () => URL.revokeObjectURL(url);
  }, [content]);

  return (
    <Box
      sx={(theme) => ({
        width: THUMB_SIZE,
        height: THUMB_SIZE,
        flexShrink: 0,
        borderRadius: 1,
        overflow: 'hidden',
        bgcolor: theme.palette.action.hover,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        mr: 2,
      })}>
      {src ? (
        <Box
          component="img"
          src={src}
          sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
        />
      ) : (
        <ImageNotSupported
          sx={(theme) => ({ color: theme.palette.action.disabled })}
        />
      )}
    </Box>
  );
};

export type LightingTypeListSelectableProps = {
  groups: LightSourceGroup[];
  onSelect: (entry: AuditLightingTypeFormEntry) => void;
  onEdit: (link: string) => void;
};

export const LightingTypeListSelectable: FC<LightingTypeListSelectableProps> =
  memo(({ groups, onSelect, onEdit }) => (
    <Box flexGrow={1} sx={{ width: '100%' }}>
      {groups.map((group) => (
        <Accordion key={group.id} variant="outlined">
          <AccordionSummary expandIcon={<ExpandMore />}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <LightbulbOutlined fontSize="small" />
              <Typography variant="body1">{group.label}</Typography>
            </Stack>
          </AccordionSummary>
          <AccordionDetails>
            <Stack>
              {group.entries.map(({ link, entry, thumbnail }, index) => (
                <Box key={link}>
                  <Grid container alignItems="center" sx={{ px: 1, py: 0 }}>
                    <Grid>
                      <EntryThumbnail content={thumbnail} />
                    </Grid>
                    <Grid size="grow">
                      <LightingTypeEntryDetails entry={entry} />
                    </Grid>
                    <Grid>
                      <Stack direction="row">
                        <IconButton
                          aria-label="edit fixture type"
                          onClick={() => onEdit(link)}>
                          <EditOutlined />
                        </IconButton>
                        <IconButton
                          aria-label="select fixture type"
                          color="primary"
                          onClick={() => onSelect(entry)}>
                          <CheckCircle />
                        </IconButton>
                      </Stack>
                    </Grid>
                  </Grid>
                  {index < group.entries.length - 1 && <Divider />}
                </Box>
              ))}
            </Stack>
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  ));
